import React, { useMemo } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import bytes from 'bytes';
import { defineMessages } from 'react-intl';

import Avatar from '@atlaskit/avatar';
import DynamicTable from '@atlaskit/dynamic-table';
import LockIcon from '@atlaskit/icon/glyph/lock';
import { RelativeDate } from '@atlassian/bitkit-date';

import RelativeLink from 'src/components/relative-link';
import RepositoryBuildStatus from 'src/components/repository-table/repository-build-status';
import { useAnalytics } from 'src/components/settings/analytics';
import NameCell from 'src/components/settings/name-cell';
import { useIntl } from 'src/hooks/intl';
import { link } from 'src/styles/mixins';
import { getPaginatedRows } from 'src/utils/get-paginated-rows';

const messages = defineMessages({
  name: {
    id: 'frontbucket.projectRepositories.page.table.name',
    description: 'Repositories table column header for repository name',
    defaultMessage: 'Name',
  },
  size: {
    id: 'frontbucket.projectRepositories.page.table.size',
    description: 'Repositories table column header for repository size',
    defaultMessage: 'Size',
  },
  lastUpdated: {
    id: 'frontbucket.projectRepositories.page.table.lastUpdated',
    description:
      'Repositories table column header for repository last updated time stamp',
    defaultMessage: 'Last updated',
  },
  builds: {
    id: 'frontbucket.projectRepositories.page.table.builds',
    description: 'Repositories table column header for repository builds',
    defaultMessage: 'Builds',
  },
  privateRepository: {
    id: 'frontbucket.projectRepositories.page.table.privateRepository',
    description:
      'Label text for the lock icon that indicates the repository is private',
    defaultMessage: 'Private repository',
  },
});

const RepoLink = styled(RelativeLink)`
  ${link};
`;

export type ProjectReposTableProps = {
  isLoading: boolean;
  page: number;
  pagelen: number;
  repositories: BB.Repository[];
  setPage: (pageNum: number) => void;
  size: number;
};

/* eslint @typescript-eslint/ban-types: "warn" */
export const ProjectRepositoriesTable: React.FC<ProjectReposTableProps> = ({
  isLoading,
  size,
  repositories,
  page,
  pagelen,
  setPage,
}) => {
  const { formatMessage } = useIntl();
  const { publishLinkClickEvent } = useAnalytics();

  const tableHeader = {
    cells: [
      {
        key: 'name',
        content: formatMessage(messages.name),
        width: 50,
      },
      {
        key: 'size',
        content: formatMessage(messages.size),
        width: 15,
      },
      {
        key: 'updated_on',
        content: formatMessage(messages.lastUpdated),
        width: 21,
      },
      {
        key: 'builds',
        content: formatMessage(messages.builds),
        width: 11,
      },
      {
        key: 'is_private',
        content: '',
        width: 3,
      },
    ],
  };

  const tableRows = useMemo(() => {
    const visibleRows = repositories.map(repo => ({
      key: `row-${repo.uuid}`,
      cells: [
        {
          key: `${repo.name}-name`,
          content: (
            <NameCell
              avatar={
                <Avatar
                  src={repo?.links?.avatar?.href}
                  size="small"
                  appearance="square"
                />
              }
              name={
                repo.links?.html.href ? (
                  <RepoLink
                    to={repo.links?.html.href}
                    onClick={() =>
                      publishLinkClickEvent('Repository', {
                        linkedRepositoryId: repo.uuid,
                      })
                    }
                  >
                    {repo.name}
                  </RepoLink>
                ) : (
                  repo.name
                )
              }
            />
          ),
        },
        {
          key: `size-${repo.size}`,
          content:
            repo.size || repo.size === 0
              ? bytes(repo.size, { unitSeparator: ' ', decimalPlaces: 1 })
              : '',
        },
        {
          key: `last-updated-${repo.updated_on}`,
          content: repo.updated_on ? (
            <RelativeDate date={repo.updated_on} />
          ) : (
            ''
          ),
        },
        {
          key: `build-status`,
          content: <RepositoryBuildStatus repository={repo} />,
        },
        {
          key: `is-private`,
          content: repo.is_private ? (
            <LockIcon
              label={formatMessage(messages.privateRepository)}
              size="medium"
            />
          ) : (
            ''
          ),
        },
      ],
    }));

    return getPaginatedRows(size, pagelen, page, visibleRows);
  }, [formatMessage, page, pagelen, publishLinkClickEvent, repositories, size]);

  return (
    <DynamicTable
      isLoading={isLoading}
      rowsPerPage={size > pagelen ? pagelen : undefined}
      page={page}
      onSetPage={setPage}
      head={tableHeader}
      rows={tableRows}
    />
  );
};
